/**
 * Modo Asesor v2 — Shell del ecosistema de despacho (Portal del Asesor).
 *
 * Recreación del `AsesorHeader` del prototipo (design_handoff_modo_asesor_v2):
 * cabecera del despacho (nombre + badge "Modo Asesor" + nº empresas + buscador
 * + "Añadir empresa" + menú de cuenta) y barra de pestañas superior en dos
 * grupos (operativas | gestión) con subrayado activo.
 *
 * A diferencia del resto del ERP, el ecosistema de despacho NO usa el rail
 * global de módulos de empresa: este shell ES la navegación a nivel firma. Lo
 * monta el dispatcher de `Layout.jsx` (rama `isFirmEcosystem`) envolviendo
 * `{children}` — igual que `AdvisorShell` para el workspace contable god-mode —
 * por eso recibe `onLogout`/`onCmdK`/… por props en vez de leerlos del contexto.
 * El gate de acceso (asesor) ya lo garantiza el dispatcher antes de montarlo.
 */

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  Menu,
  Users,
  Search,
  Bell,
  Plus,
  Calendar,
  FileCheck,
  Inbox,
  HandCoins,
  BarChart3,
  Bot,
  User as UserIcon,
  Settings,
  LogOut,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { apiFetch, unwrapOne } from '@/utils/api';
import { qk } from '@/lib/qk';
import { useAccountingFirmContext } from '@/components/hooks/useAccountingFirmContext';
import { usePortalAsesorMode } from '@/components/hooks/useCanSeePortalAsesor';
import { useUserCompaniesCount } from '@/components/hooks/useUserCompaniesCount';
import { AddClientCompanyModal } from '@/components/asesor/AddClientCompanyModal';
import { AsesorAriaChat } from '@/components/asesor/AsesorAriaChat';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

// `firmOnly`: pestañas intrínsecas de un DESPACHO (presentar modelos AEAT en
// lote con el certificado del despacho, facturar honorarios, equipo del
// despacho, notificaciones de organismos por cliente, reclamar documentación a
// clientes). En modo 'group' (grupo de sociedades propio) no aplican y se
// ocultan — ver `visibleTabs` más abajo. En grupo quedan: Empresas, Calendario,
// Informes.
const TABS = [
  { key: 'cartera', label: 'Cartera', icon: Building2, to: '/portal-asesor', group: 'op', badge: 'cartera', tone: 'red' },
  { key: 'calendario', label: 'Calendario', icon: Calendar, to: '/portal-asesor/calendario', group: 'op' },
  { key: 'modelos', label: 'Modelos', icon: FileCheck, to: '/portal-asesor/modelos', group: 'op', firmOnly: true },
  { key: 'documentos', label: 'Documentos', icon: Inbox, to: '/portal-asesor/documentacion', group: 'op', badge: 'documentos', tone: 'gray', firmOnly: true },
  { key: 'notificaciones', label: 'Notificaciones', icon: Bell, to: '/portal-asesor/notificaciones', group: 'op', badge: 'notificaciones', tone: 'red', firmOnly: true },
  { key: 'honorarios', label: 'Honorarios', icon: HandCoins, to: '/portal-asesor/honorarios', group: 'gest', badge: 'honorarios', tone: 'red', firmOnly: true },
  { key: 'equipo', label: 'Equipo', icon: Users, to: '/portal-asesor/equipo', group: 'gest', firmOnly: true },
  { key: 'informes', label: 'Informes', icon: BarChart3, to: '/portal-asesor/informes', group: 'gest' },
];

export default function AsesorShell({
  children,
  user,
  onLogout,
  onCmdK,
  onSwitchCompany,
  setMobileMenuOpen,
}) {
  const { firm, companiesCount } = useAccountingFirmContext();
  const groupCompaniesCount = useUserCompaniesCount();
  const location = useLocation();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);
  // ARIA del despacho: panel propio (cross-empresa, read-only). NO reutiliza el
  // AIPanel de empresa (`onToggleAIPanel`), que requiere una empresa activa.
  const [firmChatOpen, setFirmChatOpen] = useState(false);

  // Modo de acceso: 'firm' (despacho real) vs 'group' (grupo de sociedades
  // propio). En 'group' renombramos a "Mis empresas", ocultamos las pestañas
  // de despacho (firmOnly) y los botones que no aplican (Añadir empresa-cliente
  // y ARIA del despacho, que el backend rechaza con 403 a un no-firm-member).
  const isGroup = usePortalAsesorMode() === 'group';
  const basePath = isGroup ? '/mis-empresas' : '/portal-asesor';
  const visibleTabs = (isGroup ? TABS.filter((t) => !t.firmOnly) : TABS).map(t => ({
    ...t,
    to: t.to.replace('/portal-asesor', basePath)
  }));
  const headerTitle = isGroup ? 'Mis empresas' : (firm?.name ?? 'Mi despacho');
  const headerBadge = isGroup ? 'Mis empresas' : 'Modo Asesor';
  const empresasCount = isGroup ? groupCompaniesCount : companiesCount;

  const { data: badges } = useQuery({
    queryKey: qk.asesor.navBadges(firm?.id),
    queryFn: async () => unwrapOne(await apiFetch('/api/asesor/nav-badges')),
    enabled: !!firm?.id,
    staleTime: 2 * 60 * 1000,
  });

  const isActive = (to) =>
    to === basePath ? location.pathname === basePath : location.pathname.startsWith(to);

  return (
    <div className="flex min-h-screen flex-col">
      {/* Scrim del hueco de scroll: el pill flotante (sticky top-3) deja 12px
          por encima; este relleno evita que el contenido asome por el gap.
          Mismo patrón que el TopBar global. */}
      <div aria-hidden="true" className="sticky top-0 z-30 h-3 -mb-3 spatial-field" />
      {/* Cabecera del despacho — superficie flotante alineada con el TopBar
          del resto del ERP (margen, cristal y sombra suave). */}
      <header className="sticky top-3 z-40 mt-3 mb-1 mx-auto w-[calc(100%-1.5rem)] sm:w-[calc(100%-2rem)] lg:w-[calc(100%-3rem)] max-w-[1600px] overflow-hidden rounded-[22px] border border-border/70 bg-background/55 backdrop-blur-2xl shadow-elev-1 lg-chrome">
        <div className="flex items-center gap-1 px-3 py-2.5 sm:gap-3.5 sm:px-6 sm:py-3">
          {/* Hamburguesa: este shell SUSTITUYE al TopBar global en
              /portal-asesor y /mis-empresas, así que sin este botón la barra de
              navegación quedaba inalcanzable < lg (está fuera de pantalla y
              nada podía abrirla). */}
          {setMobileMenuOpen && (
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Abrir menú"
              data-testid="mobile-menu-trigger"
              className="-ml-1 flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
          <span className="hidden h-[34px] w-[34px] shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary sm:flex">
            <Building2 className="h-[19px] w-[19px]" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <span className="truncate text-base font-bold tracking-[-0.01em]">{headerTitle}</span>
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-[3px] text-nano font-bold uppercase tracking-[0.05em] text-primary">
                <Users className="h-[11px] w-[11px]" />
                {headerBadge}
              </span>
            </div>
            <div className="text-nano text-muted-foreground">
              {empresasCount} {empresasCount === 1 ? 'empresa' : 'empresas'}
              {isGroup ? '' : ' en cartera'}
            </div>
          </div>
          <div className="flex-1" />
          <button
            type="button"
            onClick={onCmdK}
            className="hidden h-[44px] w-40 items-center gap-2 rounded-full border border-transparent bg-muted/70 px-3 text-muted-foreground hover:bg-muted md:flex xl:w-[252px]"
          >
            <Search className="h-3.5 w-3.5 opacity-70" />
            <span className="min-w-0 flex-1 truncate text-left text-caption">Buscar empresa, CIF, modelo…</span>
            <kbd className="rounded border border-border bg-card px-1.5 py-px font-mono text-nano">⌘K</kbd>
          </button>
          {/* Añadir empresa-cliente y ARIA del despacho: solo en modo despacho.
              En grupo propio "Añadir empresa" invita a un cliente (no aplica) y
              el ARIA del despacho devuelve 403 a un no-firm-member. */}
          {!isGroup && (
            <>
              <button
                type="button"
                onClick={() => setAddOpen(true)}
                aria-label="Añadir empresa"
                className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center gap-1.5 rounded-full bg-primary px-2.5 text-caption font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 sm:px-3.5"
              >
                <Plus className="h-[15px] w-[15px]" />
                {/* Solo icono < sm: el rótulo completo empujaba el menú de
                    cuenta fuera de la cabecera en móvil. */}
                <span className="hidden sm:inline">Añadir empresa</span>
              </button>
              <button
                type="button"
                onClick={() => setFirmChatOpen((v) => !v)}
                aria-pressed={firmChatOpen}
                aria-label="Asistente ARIA del despacho"
                className={cn(
                  'flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full',
                  firmChatOpen ? 'bg-primary/10 text-primary hover:bg-primary/15' : 'text-foreground hover:bg-muted',
                )}
              >
                <Bot className="h-[16px] w-[16px]" />
              </button>
            </>
          )}
          {/* Grupo propio: aquí NO se invita a nadie (a diferencia del botón
              "Añadir empresa" de despacho arriba) — crea directamente otra
              sociedad bajo la misma cuenta, y lo hace por el MISMO embudo que
              la primera: el atajo que había antes la paría sin forma jurídica
              ni territorio fiscal. */}
          {isGroup && (
            <button
              type="button"
              onClick={() => navigate('/bienvenida?nueva=1')}
              aria-label="Nueva empresa"
              className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center gap-1.5 rounded-full bg-primary px-2.5 text-caption font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 sm:px-3.5"
            >
              <Plus className="h-[15px] w-[15px]" />
              <span className="hidden sm:inline">Nueva empresa</span>
            </button>
          )}
          {/* Menú de cuenta */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Menú de cuenta"
                data-testid="asesor-account-trigger"
                className="flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full"
              >
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt={user.full_name ?? ''} className="h-8 w-8 rounded-full object-cover" />
                ) : (
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <UserIcon className="h-4 w-4" />
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-sm">
                <div className="font-semibold">{user?.full_name ?? 'Mi cuenta'}</div>
                {user?.email && <div className="text-xs text-muted-foreground">{user.email}</div>}
              </div>
              <DropdownMenuSeparator />
              {onSwitchCompany && (
                <DropdownMenuItem onClick={onSwitchCompany} className="cursor-pointer gap-2">
                  <Building2 className="h-4 w-4" />
                  Cambiar empresa
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={() => navigate(`${basePath}/configuracion`)}
                className="cursor-pointer gap-2"
              >
                <Settings className="h-4 w-4" />
                Configuración
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onLogout} className="cursor-pointer gap-2 text-destructive">
                <LogOut className="h-4 w-4" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {/* Tabbar — scroll horizontal en móvil (8 pestañas no caben) */}
        <div className="flex items-center gap-0.5 overflow-x-auto overscroll-x-contain border-t border-border/50 px-2 pb-1 sm:px-4">
          {visibleTabs.map((t, i) => {
            const Icon = t.icon;
            const active = isActive(t.to);
            const groupBreak = i > 0 && visibleTabs[i - 1].group !== t.group;
            // En modo grupo, "Cartera" (clientes del despacho) se lee mejor como
            // "Empresas" (las sociedades propias).
            const label = isGroup && t.key === 'cartera' ? 'Empresas' : t.label;
            return (
              <div key={t.key} className="flex items-center">
                {groupBreak && <span className="mx-2.5 h-[18px] w-px shrink-0 bg-border" aria-hidden />}
                <button
                  type="button"
                  onClick={() => navigate(t.to)}
                  className={cn(
                    'relative inline-flex items-center gap-[7px] whitespace-nowrap px-3.5 py-[11px] text-body-sm transition-colors',
                    active ? 'font-semibold text-primary' : 'font-medium text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className="h-[15px] w-[15px]" />
                  {label}
                  {t.badge && badges?.[t.badge] > 0 && (
                    <span
                      className={cn(
                        'inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-full px-[5px] text-nano font-bold tabular-nums',
                        t.tone === 'red' ? 'bg-destructive text-white' : 'bg-secondary text-muted-foreground',
                      )}
                    >
                      {badges[t.badge]}
                    </span>
                  )}
                  {active && (
                    <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-primary" aria-hidden />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </header>

      {/* Contenido de la pantalla activa */}
      <div className="p-3 sm:p-5">{children}</div>

      {!isGroup && (
        <>
          <AddClientCompanyModal open={addOpen} onOpenChange={setAddOpen} firmId={firm?.id} />
          <AsesorAriaChat open={firmChatOpen} onOpenChange={setFirmChatOpen} />
        </>
      )}
    </div>
  );
}
